import { omit } from 'lodash-es';
import type { IResponse } from '~/api/interface';

const DEFAULT_PAGESIZE = 100;

/**
 * 这里key是给useAsyncData使用，防止数据有缓存存在
 */
export enum SCROLL_LIST {
  SIMILAR_CLASS = 'similar_class',
  SIMILAR_TEMPLATE = 'similar_template',
  HOME_PRIME_PLAY = 'home_prime_play',
  HOME_PRIME_VIDEO = 'home_prime_video',
  SCENARIOS_MORE_TEMPLATE = 'scenarios_moreTemplate',
  TEMPLATE_LIST = 'template_list',
  HOME_LIST = 'home_list',
  INDUSTRY_LIST = 'industry_list',
  BEST_PRACTICES = 'best_practices',
  ADD_PROJECT = 'add_project',
}

interface ScrollOption {
  pageSize?: number | Ref<number>;
  page?: number;
  key: string;
  server?: boolean;
}

type ScrollResType<
  T extends (
    ...args: any[]
  ) => Promise<IResponse<{ count?: number; results?: any[]; order_lsit?: any[] } | undefined>>,
> = T extends (...args: any[]) => Promise<
  IResponse<
    | {
        count?: number;
        results?: (infer M)[];
        order_list?: (infer M)[];
      }
    | undefined
  >
>
  ? M
  : never;

/**
 * 支持ssr
 * @param fn
 * @returns
 */
export function usePaginatedList<
  T extends (
    ...args: any[]
  ) => Promise<IResponse<{ count?: number; results?: any[]; order_list?: any[] } | undefined>>,
>(fn: T, option: ScrollOption) {
  const { pageSize: _pageSize, key, server } = option || {};

  type L = ScrollResType<T>;

  const pageSize = isRef(_pageSize) ? _pageSize : ref(_pageSize || DEFAULT_PAGESIZE);

  const pre = ref([]) as Ref<L[]>;

  const current = ref(option.page || 1);

  const {
    data,
    refresh: _refresh,
    execute: _execute,
    pending,
    clear: _clear,
  } = useAsyncData(
    key,
    async () => {
      const res = await fn({
        page_size: pageSize.value,
        page: current.value,
      });

      const { count: _count = 0, results = [], order_list = [] } = res?.data || {};

      return {
        count: _count,
        results: (results.length ? results : order_list).map(v =>
          omit(v as any, ['seo_description', 'seo_title', 'seo_keywords']),
        ) as L[],
      };
    },
    { server, lazy: true },
  );

  function refresh() {
    // pageSize.value = DEFAULT_PAGESIZE
    current.value = 1;
    pre.value = [];
    data.value = { count: data.value?.count || 0, results: [] };
    return _refresh();
  }

  const list = computed<L[]>(() => {
    if (data.value) {
      return pre.value.concat(data.value.results);
    }
    return pre.value;
  });

  const total = computed(() => {
    return data.value?.count || 0;
  });

  async function next(page: number = current.value + 1) {
    if (pending.value) {
      return;
    }
    current.value = page;
    await _execute();
  }

  async function changePage(page: number, page_size: number) {
    if (pending.value) {
      return;
    }
    current.value = page;
    pageSize.value = page_size;
    await _execute();
  }

  function clear() {
    _clear();
    pre.value = [];
    current.value = 1;
  }

  return { list, pending, refresh, next, changePage, total, current, pageSize, clear };
}
